import { createRootRoute, Outlet, useLocation } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { HelmetProvider } from 'react-helmet-async';
import { initAnalytics } from '@/lib/analytics';
import {
  captureAttributionFingerprint,
  matchWebAttribution,
} from '@/lib/attribution';
import { createAppQueryClient } from '@/lib/query-client';
import { useEffect } from 'react';

const queryClient = createAppQueryClient();

const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const clerkSignInUrl = import.meta.env.VITE_CLERK_SIGN_IN_URL ?? '/auth/sign-in';
const clerkSignUpUrl = import.meta.env.VITE_CLERK_SIGN_UP_URL ?? '/auth/sign-up';

// Initialize analytics synchronously at module load time
// This ensures it's ready before any components try to track events
initAnalytics();
captureAttributionFingerprint();

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function WebAttributionMatch() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    void matchWebAttribution(getToken);
  }, [isLoaded, isSignedIn, getToken]);
  return null;
}

function RootComponent() {

  // Show error if Clerk key is missing (except in production where it's required)
  if (!clerkPublishableKey) {
    return (
      <div className='min-h-screen flex items-center justify-center p-6'>
        <div className='text-center max-w-md'>
          <h1 className='text-xl font-bold text-gray-900 mb-2'>
            Configuration Error
          </h1>
          <p className='text-gray-600'>
            Missing VITE_CLERK_PUBLISHABLE_KEY. Add it to your .env file and
            restart the development server.
          </p>
        </div>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <ClerkProvider
        publishableKey={clerkPublishableKey}
        signInUrl={clerkSignInUrl}
        signUpUrl={clerkSignUpUrl}
      >
        <QueryClientProvider client={queryClient}>
          <ScrollToTop />
          <WebAttributionMatch />
          <div className='flex flex-col min-h-screen'>
            <main className='flex-1'>
              <Outlet />
            </main>
          </div>
        </QueryClientProvider>
      </ClerkProvider>
    </HelmetProvider>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
});
